import React, { useCallback, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel } from "devextreme-react";

import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
// The shared error-message ladder (Architecture.md rule 16). It already covers everything a local helper
// would: axios errors with a response body, PascalCase or camelCase Message, string bodies, and the
// timeout case where error.response is undefined.
import { getApiErrorMessage } from "../../../../../common/concurrency";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import OperationResultGrid from "../../../charges/common/OperationResultGrid";
import { ParametersLayout } from "./Layout";
import { GRID_COLUMNS, GRID_KEY_EXPR } from "./GridColumns";
import "./deleteItemTasks.css";
import {
    BUTTON,
    DEFAULT_LAYOUT_NAME,
    FIELD,
    GRID_KEY,
    HELP_TEXT,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_TITLE,
    REQUEST_TIMEOUT_MS,
    toDateOnly,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", LOADING: "loading", DELETING: "deleting" });

/**
 * The empty row set, as ONE module-level array.
 *
 * A fresh `[]` on every clear would be a new prop identity each time, which defeats the memo on
 * OperationResultGrid — and a re-render there rebuilds the grid's toolbar template, remounting
 * GridLayoutManager and firing another GetGridLayouts. Not frozen: DevExtreme is free to read this as a
 * dataSource and Object.freeze would turn any internal in-place work into a throw.
 */
const NO_ROWS = [];

/**
 * A function, not a module-level constant: Reset must re-evaluate new Date(), and a shared object would
 * be mutated across resets.
 *
 * Defaults are FormCreate's — and btnResetFilterClick sets the same two, which is why Reset and first
 * load share this one function.
 */
const initialFormFields = () => ({
    [FIELD.StartDate]: new Date(),
    [FIELD.NumberOfDays]: 0,
});

/** The number of days as the API wants it: a whole number, never a string or NaN. */
const toDays = (value) => {
    const days = Number(value);
    return Number.isFinite(days) ? Math.trunc(days) : 0;
};

/**
 * Identity of the inputs a given grid load reflects, so the page can tell whether what is on screen still
 * answers the question the user is now asking.
 */
const inputsKey = (fields) => `${toDateOnly(fields[FIELD.StartDate]) ?? ""}|${toDays(fields[FIELD.NumberOfDays])}`;

/**
 * WM "Delete Item Tasks" (legacy WMD0124).
 *
 * Start permanently deletes the WM item tasks whose work is finished and whose cut-off has passed, via
 * SPWM_DELETETASKRECORDS. A task qualifies either because it is a committed Location Transfer completed
 * before the cut-off, or because the transaction header it hangs off was committed and
 * received/shipped before it.
 *
 * <b>The grid is the delete list, not a filter preview.</b> AI8 binds it to the same query the Start gate
 * counts and re-opens it after the run, so the user watches the rows go. That is the opposite of the
 * other Periodic Tasks screens, whose Preview answers a different question from their Start — do not
 * read the two alike.
 *
 * There is no Record Range here: uWMD0124.dfm has no TAbWhereBuilder at all. The Start Date and Number
 * Days are the entire input, which is why this page has no <CommonReport> and no filter plumbing.
 *
 * This is an OPERATION screen: no CustomStore, no <Editing>, no per-row read-modify-write, so
 * Architecture.md rules 4-6, 12 and 14-16 (including optimistic concurrency) do not apply.
 *
 * The legacy Help and Exit buttons are dropped: Help only ever said "Help on selected topic is not
 * available at this time.", and Exit closed the MDI child.
 */
const DeleteItemTasks = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const gridRef = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [busy, setBusy] = useState(BUSY.IDLE);
    const [gridLayout, setGridLayout] = useState(DEFAULT_LAYOUT_NAME);

    /**
     * One object rather than three useStates. React 17 does not batch setState across an await, so
     * separate updates after the fetch meant extra renders of the page — and each one rebuilt the grid's
     * toolbar template, remounting GridLayoutManager and firing another GetGridLayouts.
     *
     * `loadedFor` is the inputs the rows answer: null until the first Preview, and compared against the
     * current inputs so Start can tell whether the user is about to delete what they actually looked at.
     */
    const [tasks, setTasks] = useState({ rows: NO_ROWS, hasRun: false, loadedFor: null });

    const isBusy = busy !== BUSY.IDLE;

    const loadPanelMessage = busy === BUSY.DELETING
        ? MESSAGES.DELETE_TASKS_DELETING
        : MESSAGES.DELETE_TASKS_LOADING;

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    /**
     * Preview and Start send the identical body, so the rows the user is shown and the rows the procedure
     * is asked to delete come from the same two values.
     *
     * The date goes through toDateOnly, which keeps a calendar date from drifting a day across time zones
     * — on this screen that is a day's worth of tasks. See constants.js.
     */
    const buildRequest = useCallback(
        () => ({
            [FIELD.StartDate]: toDateOnly(formFields[FIELD.StartDate]),
            [FIELD.NumberOfDays]: toDays(formFields[FIELD.NumberOfDays]),
        }),
        [formFields]
    );

    /**
     * Required: FormField calls onFormLoad() in fetchData's `finally` with no guard and no default
     * (FormField.js:256), so omitting it throws "onFormLoad is not a function" and the parameters panel
     * never renders.
     *
     * Nothing to do inside it — there are no select options to inject and no enablement rules, unlike
     * every sibling operation screen. Kept as an explicit no-op rather than omitted, with this note, so
     * the next reader does not "tidy it away" and break the panel.
     */
    const handleFormLoad = useCallback(() => {}, []);

    /**
     * The checks both buttons run before they post. Shows the same red toast the server would, without
     * spending a round trip to be told what the screen already knows.
     *
     * Both are deliberate narrowings of AI8 and the server repeats both — see the service's
     * ValidateRequest for why a null date and a negative day count are worth refusing rather than
     * running.
     */
    const inputsAreUsable = useCallback(() => {
        if (!formFields[FIELD.StartDate]) {
            notify({ message: MESSAGES.DELETE_TASKS_START_DATE_REQUIRED, width: 450 }, "error", 5000);
            return false;
        }

        if (toDays(formFields[FIELD.NumberOfDays]) < 0) {
            notify({ message: MESSAGES.DELETE_TASKS_NUMBER_DAYS_INVALID, width: 450 }, "error", 5000);
            return false;
        }

        return true;
    }, [formFields]);

    /**
     * Loads the grid. Shared by the Preview button and by the refresh Start does afterwards, so the two
     * cannot end up showing different things.
     *
     * Returns the row count so Start can decide what to say; returns null when the request failed, which
     * is not the same as zero rows and must not be reported as "nothing qualifies".
     */
    const loadTasks = useCallback(async () => {
        setBusy(BUSY.LOADING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMDeleteItemTasks.GetRecords}`,
                JSON.stringify(buildRequest()),
                postOptions()
            );

            const payload = response?.data;

            // A row array is the ONLY shape treated as success; anything else is an error, whatever the
            // envelope claims. Two failure modes reach here as HTTP 200: a refusal decided BEFORE the
            // query runs, which arrives as Success:false with no Resultdata; and one that fails DURING
            // the query, which a Success-only check misses. Requiring the array closes both.
            if (payload?.Success === false || !Array.isArray(payload?.Resultdata)) {
                setTasks({ rows: NO_ROWS, hasRun: false, loadedFor: null });
                notify({ message: payload?.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return null;
            }

            // An empty array IS legitimate — the grid says so via noDataText.
            setTasks({ rows: payload.Resultdata, hasRun: true, loadedFor: inputsKey(formFields) });
            return payload.Resultdata.length;
        } catch (error) {
            setTasks({ rows: NO_ROWS, hasRun: false, loadedFor: null });
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
            return null;
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [buildRequest, postOptions, formFields]);

    const handlePreview = useCallback(async () => {
        if (!inputsAreUsable()) {
            return;
        }

        await loadTasks();
    }, [inputsAreUsable, loadTasks]);

    /**
     * Start: ask, run, then reload the grid — the last step reproducing the trailing btnPreviewClick in
     * uWMD0124.pas, which is how AI8 shows the user that the rows are gone.
     *
     * Two questions. The first is the Delphi's own, verbatim. The second is the web's addition and only
     * fires when the grid does not already reflect the current inputs: on this screen the grid IS the
     * delete list, so pressing Start without having looked at it — or after changing the dates since —
     * is the one mistake worth interrupting. AI8 hid that by re-running Preview itself before asking;
     * doing the same here would mean two server round trips before the user has agreed to anything.
     */
    const handleStart = useCallback(async () => {
        // Before the confirmations: no point asking someone to acknowledge a run that could not proceed.
        if (!inputsAreUsable()) {
            return;
        }

        if (tasks.loadedFor !== inputsKey(formFields)
            && (await confirm(MESSAGES.DELETE_TASKS_NOT_PREVIEWED_CONFIRM, MESSAGES.CONFIRM)) !== true) {
            return;
        }

        if ((await confirm(MESSAGES.DELETE_TASKS_CONFIRM, MESSAGES.CONFIRM)) !== true) {
            return;
        }

        setBusy(BUSY.DELETING);
        let succeeded = false;
        let message = null;

        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMDeleteItemTasks.Start}`,
                JSON.stringify(buildRequest()),
                postOptions()
            );

            const payload = response?.data;
            const data = payload?.Resultdata;

            succeeded = payload?.Success === true && (data?.TasksDeleted ?? 0) > 0;
            message = payload?.Message || (succeeded ? "" : MESSAGES.NO_RECORD_FOUND_RANGE);
        } catch (error) {
            message = getApiErrorMessage(error, MESSAGES.ERROR);
        } finally {
            setBusy(BUSY.IDLE);
        }

        notify({ message, width: 450 }, succeeded ? "success" : "error", 5000);

        // Unconditionally, as the Delphi does: after a failure the grid showing the same rows still
        // standing is itself the useful answer. Sequenced after setBusy(IDLE) rather than inside the
        // try, so the load panel's message switches from "Deleting" to "Loading" instead of the user
        // watching a delete message while a read runs.
        await loadTasks();
    }, [inputsAreUsable, tasks.loadedFor, formFields, buildRequest, postOptions, loadTasks]);

    /**
     * Reset: both questions back to their load-time defaults, and the grid emptied.
     *
     * btnResetFilterClick only resets the two questions — it leaves the grid showing whatever it last
     * loaded. Clearing it here is deliberate: rows that answered the old dates, sitting under new ones,
     * are exactly the stale delete list the Start confirmation exists to catch, and there is no reason to
     * create that state on purpose.
     */
    const handleReset = useCallback(() => {
        setFormFields(initialFormFields());
        setTasks({ rows: NO_ROWS, hasRun: false, loadedFor: null });
    }, []);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={loadPanelMessage}
            />

            {/*
                Delphi's grpMain is a centred bold title band above the grid. In the web app the page
                title lives in the toolbar, so the band collapses into this header rather than being
                reproduced as a separate strip.
            */}
            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Start} type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Reset} onClick={handleReset} disabled={isBusy} />
                        </Item>
                    </Toolbar>
                </div>
            </div>

            <div className="dit-page">
                {/* The Delphi criteria memo (edtMemo), at the top of the form under the title. */}
                <div className="op-help dit-help">{HELP_TEXT}</div>

                {/*
                    Grid above the questions, which is the Delphi's own order: grdSchedules fills the
                    middle of the form and pnlBottom sits under it. Unusual for a web screen — the inputs
                    normally come first — but the grid's height is bounded (see the stylesheet) so both are
                    on screen together exactly as they are in AI8, and moving the questions above it would
                    put this screen's layout at odds with the form it replaces for no gain.

                    Deliberately NOT loaded on mount. AI8 opens with the query closed (NoRecsExpr '0=1')
                    and waits for Preview, which matters here more than it would elsewhere: the rows are a
                    delete list, and presenting one before the user has chosen their dates invites them to
                    press Start on defaults they never looked at.
                */}
                <div className="dit-grid">
                    <OperationResultGrid
                        rows={tasks.rows}
                        columns={GRID_COLUMNS}
                        keyExpr={GRID_KEY_EXPR}
                        noDataText={tasks.hasRun ? MESSAGES.DELETE_TASKS_NO_RECORDS_FOUND : ""}
                        height="100%"
                        gridRef={gridRef}
                        gridKey={GRID_KEY}
                        currentLayout={gridLayout}
                        setCurrentLayout={setGridLayout}
                        accessToken={accessToken}
                    />
                </div>

                {/*
                    Delphi's pnlBottom: grpBottomLeft carries the two questions and grpBottomRight is an
                    empty balancing box, so there is one form group and nothing beside it.

                    hasOnlyOneColumn because of that. Without it FormField gives the group col-md-4, i.e.
                    a third of the page, for two fields.
                */}
                <div className="op-parameters dit-parameters">
                    <FormField
                        currrentUser={currentUser}
                        layoutId={LAYOUT_ID}
                        layoutName={LAYOUT_NAME}
                        defaultFormSchema={ParametersLayout}
                        formFields={formFields}
                        setformFields={setFormFields}
                        DYNAMICFORMSETTING={dynamicFormSetting}
                        formRefs={formRefs}
                        onFormLoad={handleFormLoad}
                        hasOnlyOneColumn={true}
                    />
                </div>
            </div>
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(DeleteItemTasks);
